// Nutrition capture handler — the second module registered in the
// universal-capture dispatcher, cloned from agendaCaptureHandler. Maps the
// propose_nutrition_log proposal (raw spoken meal) through the nutrition-extract
// edge fn into a NutritionPrefill, then opens NutritionLogModal for review.
// Behavior parity with agenda: 402 -> shared quota gate; no usable result ->
// null (silent, Sonnet's text already rendered); the MODAL owns the DB write.

import NutritionLogModal, { type NutritionPrefill } from "@/components/nutrition/NutritionLogModal";
import { supabase } from "@/integrations/supabase/client";
import type { MealType } from "@/hooks/useNutrition";
import type { ModuleCaptureHandler } from "@/lib/capture/types";

// Shape nutrition-extract returns (mirrors the edge fn schema).
interface ExtractedNutrition {
  description: string;
  meal_type_guess: string;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  confidence: number;
  ingredients_detected: string[];
}

const VALID_MEAL_TYPES: MealType[] = [
  "breakfast", "lunch", "dinner", "snack", "pre_workout", "post_workout",
];

function toMealType(guess: string): MealType {
  return (VALID_MEAL_TYPES as string[]).includes(guess) ? (guess as MealType) : "snack";
}

export const nutritionCaptureHandler: ModuleCaptureHandler<NutritionPrefill> = {
  tool: "propose_nutrition_log",

  getRawText: (proposal) =>
    proposal.tool === "propose_nutrition_log"
      ? (proposal.args.raw_text ?? "").trim()
      : "",

  extract: async (rawText, proposal, ctx) => {
    // Pass the user's timezone + "now" so the extractor can use the local hour
    // as a meal_type tiebreaker (same pattern agenda-extract uses for dates).
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const referenceDateIso = new Date().toISOString();
    const { data, error } = await supabase.functions.invoke("nutrition-extract", {
      body: {
        transcript: rawText,
        language: ctx.lang,
        user_timezone: userTimezone,
        reference_date_iso: referenceDateIso,
      },
    });
    if (error) {
      // Parity with agenda: surface a 402 quota/module error as a paywall,
      // else warn. null aborts the capture cleanly.
      if (await ctx.surfaceQuota(error)) return null;
      console.warn("[capture/nutrition] nutrition-extract failed", error);
      return null;
    }

    const r = data as ExtractedNutrition | null;
    if (!r || typeof r.kcal !== "number" || typeof r.confidence !== "number") {
      console.info("[capture/nutrition] nutrition-extract returned no usable result");
      return null;
    }
    // Not-food guard: the extractor marks non-meals as confidence<0.3 + kcal 0.
    // Anything that looks even loosely like a meal still opens the modal so the
    // user can correct the macros (matches the photo flow's low-confidence UX).
    if (r.confidence < 0.25 && r.kcal === 0) {
      console.info("[capture/nutrition] transcript not recognized as a meal");
      return null;
    }

    const actionId = proposal.action_id;
    return {
      description: r.description,
      meal_type: toMealType(r.meal_type_guess),
      kcal: Math.max(0, Math.round(r.kcal)),
      protein: Math.max(0, Math.round(r.protein)),
      carbs: Math.max(0, Math.round(r.carbs)),
      fat: Math.max(0, Math.round(r.fat)),
      confidence: r.confidence,
      ingredients_detected: Array.isArray(r.ingredients_detected) ? r.ingredients_detected : [],
      source: "voice",
      client_action_id: "mirror_msg_" + actionId,
      transcriptRaw: rawText,
    };
  },

  renderModal: ({ open, prefill, onClose, onCreated }) => (
    <NutritionLogModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated} />
  ),
};
